import { apiClient } from './client';
import type {
  ABCRow,
  BatchInventoryRow,
  CurrentStockRow,
  DeadSlowRow,
  ExpiryBatchesResponse,
  ExpiryBatchFilters,
  ExpiryGridFilters,
  ExpiryGridResponse,
  ExpiryOverviewResponse,
  MovementLedgerRow,
  PaginatedResponse,
  PharmaFilters,
  ReorderConfigTemplateRow,
  ReorderParams,
  ReorderPolicyInput,
  ReorderPolicyRow,
  ReorderPolicyScopeInput,
  ReorderPolicyScopeRow,
  ReorderPolicyScopeType,
  ReorderRow,
  ReorderScopeOption,
  StockAgeingRow,
  TurnoverRow,
  XYZRow,
} from './pharma-reports/inventory.types';
import type {
  AlertItem,
  DashboardKPIs,
  DaySummaryFilters,
  DaySummaryLowMarginFilters,
  DaySummaryLowMarginResponse,
  DaySummaryResponse,
  ExecutiveSalesDashboardResponse,
  ExpiryLossTrendPoint,
  InventoryValueTrendPoint,
} from './pharma-reports/dashboard.types';
import type {
  City360Report,
  Customer360Report,
  Item360Report,
  Route360Report,
  SalesTeam360Report,
  Supplier360Report,
  ThreeSixtyDrillFilters,
  ThreeSixtyDrillResponse,
  ThreeSixtyPeriod,
  ThreeSixtySearchOption,
  ThreeSixtySearchType,
} from './pharma-reports/three-sixty.types';
import type {
  StockOutReportResponse,
  SuggestedPurchaseRow,
  SupplierPerformancePurchaseInvoiceDetailRow,
  SupplierPerformancePurchaseOrderDetailRow,
  SupplierPerformanceReportResponse,
} from './pharma-reports/procurement.types';
import type {
  ComparisonMonthCellsFilters,
  ComparisonMonthCellsResponse,
  DegrowthMemberFilters,
  DegrowthMemberResponse,
  MonthlyPivotFilters,
  MonthlyPivotResponse,
  SalesDegrowthResponse,
  SalesPurchaseAnalysisKind,
  SalesPurchaseBillDrilldown,
  SalesPurchaseBillRow,
  SalesPurchaseComparisonResponse,
  SalesPurchaseDimension,
  SalesPurchaseDimensionResponse,
  SalesPurchaseItemDrilldown,
  SalesPurchaseOverviewResponse,
  SalesPurchasePartyDrilldown,
} from './pharma-reports/sales-analysis.types';
import type {
  AccountLedgerResponse,
  FinancialOutstandingDetailResponse,
  FinancialOutstandingGroupResponse,
  FinancialOutstandingResponse,
  FinancialPartyLedgerResponse,
  FinancialPartyType,
  SalesmanOutstandingBillsResponse,
  SalesmanOutstandingResponse,
  TrialBalanceResponse,
} from './pharma-reports/financial.types';

export type {
  PaginatedResponse,
  PharmaFilters,
  CurrentStockRow,
  BatchInventoryRow,
  MovementLedgerRow,
  ReorderRow,
  DemandBasis,
  ReorderParams,
  ReorderPolicyRow,
  ReorderConfigTemplateRow,
  ReorderPolicyInput,
  ReorderPolicyScopeType,
  ReorderPolicyScopeRow,
  ReorderPolicyScopeInput,
  ReorderScopeOption,
  StockAgeingRow,
  ExpiryDimension,
  ExpiryStatus,
  ExpiryColumn,
  ExpiryWindowFigures,
  ExpiryOverviewResponse,
  ExpiryGridRow,
  ExpiryGridResponse,
  ExpiryScope,
  ExpiryGridFilters,
  ExpiryBatchFilters,
  ExpiryBatchRow,
  ExpiryBatchesResponse,
  DeadSlowRow,
  ABCRow,
  XYZRow,
  TurnoverRow,
} from './pharma-reports/inventory.types';
export type {
  SuggestedPurchaseRow,
  SupplierPerformanceRow,
  StockOutRow,
  ProcurementDataAvailability,
  ProcurementDataSyncAnalysis,
  SupplierPerformanceReportResponse,
  SupplierPerformancePurchaseOrderDetailRow,
  SupplierPerformancePurchaseInvoiceDetailRow,
  StockOutReportResponse,
} from './pharma-reports/procurement.types';
export type {
  SalesPurchaseAnalysisKind,
  SalesPurchaseOverviewResponse,
  SalesPurchaseBillRow,
  SalesPurchaseBillDrilldown,
  SalesPurchaseItemDrilldown,
  SalesPurchasePartyDrilldown,
  SalesPurchaseDimension,
  SalesPurchaseDimensionRow,
  SalesPurchaseDimensionResponse,
  SalesPurchaseComparisonSummary,
  SalesPurchaseComparisonBreakdownRow,
  SalesPurchaseComparisonResponse,
  DegrowthSignal,
  SalesDegrowthRow,
  SalesDegrowthDimension,
  ComparisonMonthGrid,
  ComparisonMonthCellsFilters,
  ComparisonMonthCellsResponse,
  SalesDegrowthResponse,
  MonthlyPivotDimension,
  MonthlyPivotMetrics,
  MonthlyPivotRow,
  MonthlyPivotResponse,
  MonthlyPivotFilters,
  DegrowthBreakdown,
  DegrowthMemberFilters,
  DegrowthMemberCustomerRow,
  DegrowthMemberResponse,
} from './pharma-reports/sales-analysis.types';
export type {
  FinancialPartyType,
  FinancialBucketDefinition,
  FinancialDsoMetric,
  FinancialTopOverdueRow,
  FinancialOutstandingSummary,
  FinancialOutstandingPartyRow,
  FinancialOutstandingResponse,
  FinancialOutstandingGroupRow,
  FinancialOutstandingGroupResponse,
  FinancialOutstandingInvoiceRow,
  FinancialOutstandingDetailResponse,
  FinancialLedgerTransactionRow,
  FinancialPartyLedgerResponse,
  SalesmanOutstandingRow,
  SalesmanOutstandingResponse,
  SalesmanOutstandingBillRow,
  SalesmanOutstandingBillsResponse,
  TrialBalanceRow,
  TrialBalanceSummary,
  TrialBalanceResponse,
  AccountLedgerRow,
  AccountLedgerResponse,
} from './pharma-reports/financial.types';
export type {
  DashboardKPIs,
  AlertItem,
  InventoryValueTrendPoint,
  ExpiryLossTrendPoint,
  ExecutiveSalesDashboardResponse,
  DaySummaryDelta,
  DaySummaryHealthFactor,
  DaySummaryAgeingBucket,
  DaySummaryExposure,
  DaySummaryAlert,
  DaySummaryResponse,
  DaySummaryLowMarginBillRow,
  DaySummaryLowMarginResponse,
  DaySummaryLowMarginFilters,
  DaySummaryFilters,
} from './pharma-reports/dashboard.types';
export type {
  ThreeSixtyPeriod,
  ThreeSixtySearchType,
  ThreeSixtySearchOption,
  ThreeSixtyMetricTrendPoint,
  ReportMonthFields,
  ThreeSixtyMonthGrid,
  ThreeSixtyItemMovementRow,
  ThreeSixtyMonthTable,
  ThreeSixtyMovementBasis,
  ThreeSixtyMovementLists,
  ThreeSixtyMovementByDimension,
  ThreeSixtyContributionRow,
  ThreeSixtyItemMappingDiagnostic,
  ThreeSixtyAgeingRow,
  Item360Report,
  Customer360Report,
  Supplier360Report,
  DimensionSalesReport,
  Route360Report,
  ThreeSixtyDrillReport,
  ThreeSixtyDrillOrigin,
  ThreeSixtyDrillLevel,
  ThreeSixtyDrillFilters,
  ThreeSixtyDrillResponse,
  City360Report,
  SalesmanCustomerOutstandingRow,
  SalesTeam360Report,
} from './pharma-reports/three-sixty.types';

// ── API Service ────────────────────────────────────────────────────────────

const BASE = '/pharma-reports';

const THREE_SIXTY_REPORT_TIMEOUT_MS = 300000;

function toParams(filters: Record<string, unknown>): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      v.forEach((item) => {
        params[k] = String(item); // will be handled by axios paramsSerializer
      });
    } else {
      params[k] = String(v);
    }
  }
  return params;
}

export const pharmaReportsService = {
  // ── Dashboard ──────────────────────────────────────────────────────────
  getDashboardKPIs: (filters?: PharmaFilters) =>
    apiClient.get<DashboardKPIs>(`${BASE}/dashboard/kpis`, { params: toParams(filters ?? {}) }).then((r) => r.data),

  getExpiryLossTrend: (filters?: PharmaFilters) =>
    apiClient
      .get<ExpiryLossTrendPoint[]>(`${BASE}/dashboard/expiry-loss-trend`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getInventoryValueTrend: () =>
    apiClient.get<InventoryValueTrendPoint[]>(`${BASE}/dashboard/inventory-value-trend`).then((r) => r.data),

  getItem360: (filters?: { search?: string; period?: ThreeSixtyPeriod; locationId?: string }) =>
    apiClient
      .get<Item360Report>(`${BASE}/360/item`, {
        params: toParams(filters ?? {}),
        timeout: THREE_SIXTY_REPORT_TIMEOUT_MS,
      })
      .then((r) => r.data),

  getCustomer360: (filters?: { search?: string; period?: ThreeSixtyPeriod; locationId?: string }) =>
    apiClient
      .get<Customer360Report>(`${BASE}/360/customer`, {
        params: toParams(filters ?? {}),
        timeout: THREE_SIXTY_REPORT_TIMEOUT_MS,
      })
      .then((r) => r.data),

  getSupplier360: (filters?: { search?: string; period?: ThreeSixtyPeriod; locationId?: string }) =>
    apiClient
      .get<Supplier360Report>(`${BASE}/360/supplier`, {
        params: toParams(filters ?? {}),
        timeout: THREE_SIXTY_REPORT_TIMEOUT_MS,
      })
      .then((r) => r.data),

  getRoute360: (filters?: { search?: string; period?: ThreeSixtyPeriod }) =>
    apiClient
      .get<Route360Report>(`${BASE}/360/route`, {
        params: toParams(filters ?? {}),
        timeout: THREE_SIXTY_REPORT_TIMEOUT_MS,
      })
      .then((r) => r.data),

  getCity360: (filters?: { search?: string; period?: ThreeSixtyPeriod }) =>
    apiClient
      .get<City360Report>(`${BASE}/360/city`, {
        params: toParams(filters ?? {}),
        timeout: THREE_SIXTY_REPORT_TIMEOUT_MS,
      })
      .then((r) => r.data),

  getSalesTeam360: (filters?: { search?: string; period?: ThreeSixtyPeriod }) =>
    apiClient
      .get<SalesTeam360Report>(`${BASE}/360/salesman`, {
        params: toParams(filters ?? {}),
        timeout: THREE_SIXTY_REPORT_TIMEOUT_MS,
      })
      .then((r) => r.data),

  getThreeSixtyDrill: (filters: ThreeSixtyDrillFilters) =>
    apiClient
      .get<ThreeSixtyDrillResponse>(`${BASE}/360/drill`, {
        params: toParams(filters as unknown as Record<string, unknown>),
        timeout: THREE_SIXTY_REPORT_TIMEOUT_MS,
      })
      .then((r) => r.data),

  search360Options: (filters: { type: ThreeSixtySearchType; search?: string; limit?: number }) =>
    apiClient.get<ThreeSixtySearchOption[]>(`${BASE}/360/search`, { params: toParams(filters) }).then((r) => r.data),

  // ── Inventory ──────────────────────────────────────────────────────────
  getCurrentStock: (filters?: PharmaFilters) =>
    apiClient
      .get<PaginatedResponse<CurrentStockRow>>(`${BASE}/inventory/current-stock`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getBatchInventory: (filters?: PharmaFilters) =>
    apiClient
      .get<PaginatedResponse<BatchInventoryRow>>(`${BASE}/inventory/batch-wise`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getMovementLedger: (filters?: PharmaFilters) =>
    apiClient
      .get<
        PaginatedResponse<MovementLedgerRow>
      >(`${BASE}/inventory/movement-ledger`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getReorderReport: (filters?: PharmaFilters & ReorderParams & { avgSalesDays?: number }) =>
    apiClient
      .get<PaginatedResponse<ReorderRow>>(`${BASE}/inventory/reorder`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getReorderConfig: (filters?: PharmaFilters) =>
    apiClient
      .get<PaginatedResponse<ReorderPolicyRow>>(`${BASE}/inventory/reorder-config`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getReorderPolicyScopes: (filters?: PharmaFilters & { scopeType?: ReorderPolicyScopeType }) =>
    apiClient
      .get<
        PaginatedResponse<ReorderPolicyScopeRow>
      >(`${BASE}/inventory/reorder-config/scopes`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getReorderScopeOptions: (params: { scopeType: ReorderPolicyScopeType; search?: string; limit?: number }) =>
    apiClient
      .get<ReorderScopeOption[]>(`${BASE}/inventory/reorder-config/scope-options`, { params: toParams(params) })
      .then((r) => r.data),

  upsertReorderConfig: (rows: ReorderPolicyInput[]) =>
    apiClient
      .post<{
        upserted: number;
        skipped: Array<{ row: number; reason: string }>;
      }>(`${BASE}/inventory/reorder-config`, { rows })
      .then((r) => r.data),

  upsertReorderPolicyScopes: (rows: ReorderPolicyScopeInput[]) =>
    apiClient
      .post<{
        upserted: number;
        skipped: Array<{ row: number; reason: string }>;
      }>(`${BASE}/inventory/reorder-config/scopes`, { rows })
      .then((r) => r.data),

  getReorderConfigTemplate: (filters?: PharmaFilters) =>
    apiClient
      .get<ReorderConfigTemplateRow[]>(`${BASE}/inventory/reorder-config/template`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  deleteReorderConfig: (productId: string, locationId: string) =>
    apiClient
      .delete<{ deleted: number }>(`${BASE}/inventory/reorder-config`, { params: { productId, locationId } })
      .then((r) => r.data),

  deleteReorderPolicyScope: (id: string) =>
    apiClient.delete<{ deleted: number }>(`${BASE}/inventory/reorder-config/scopes/${id}`).then((r) => r.data),

  getStockAgeing: (filters?: PharmaFilters & { bucketDays?: number[] }) =>
    apiClient
      .get<{
        data: StockAgeingRow[];
        summary: { bucket: string; total_qty: number; total_value: number }[];
        total: number;
        totals?: Record<string, number>;
      }>(`${BASE}/inventory/ageing`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  // ── Expiry ─────────────────────────────────────────────────────────────
  getExpiryOverview: (filters: { asOf: string }) =>
    apiClient.get<ExpiryOverviewResponse>(`${BASE}/expiry/overview`, { params: toParams(filters) }).then((r) => r.data),

  getExpiryGrid: (filters: ExpiryGridFilters) =>
    apiClient
      .get<ExpiryGridResponse>(`${BASE}/expiry/grid`, {
        params: toParams(filters as unknown as Record<string, unknown>),
      })
      .then((r) => r.data),

  getExpiryBatches: (filters: ExpiryBatchFilters) =>
    apiClient
      .get<ExpiryBatchesResponse>(`${BASE}/expiry/batches`, {
        params: toParams(filters as unknown as Record<string, unknown>),
      })
      .then((r) => r.data),

  // ── Stock Analysis ─────────────────────────────────────────────────────
  getDeadSlowStock: (filters?: PharmaFilters & { deadMonths?: number }) =>
    apiClient
      .get<PaginatedResponse<DeadSlowRow>>(`${BASE}/analysis/dead-slow`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getABCAnalysis: (filters?: PharmaFilters & { thresholdA?: number; thresholdB?: number; periodMonths?: number }) =>
    apiClient
      .get<{
        data: ABCRow[];
        summary: { class: string; count: number; value: number; pct: number }[];
      }>(`${BASE}/analysis/abc`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getXYZAnalysis: (filters?: PharmaFilters & { thresholdX?: number; thresholdY?: number; periodMonths?: number }) =>
    apiClient.get<{ data: XYZRow[] }>(`${BASE}/analysis/xyz`, { params: toParams(filters ?? {}) }).then((r) => r.data),

  getInventoryTurnover: (filters?: PharmaFilters) =>
    apiClient
      .get<PaginatedResponse<TurnoverRow>>(`${BASE}/analysis/turnover`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  // ── Procurement ────────────────────────────────────────────────────────
  getSuggestedPurchase: (filters?: PharmaFilters & ReorderParams) =>
    apiClient
      .get<
        PaginatedResponse<SuggestedPurchaseRow>
      >(`${BASE}/procurement/suggested-purchase`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getSupplierPerformance: (filters?: PharmaFilters & { supplierIds?: string[] }) =>
    apiClient
      .get<SupplierPerformanceReportResponse>('/reports/supplier-performance', { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getSupplierPerformancePurchaseOrders: (supplierKey: string, filters?: PharmaFilters & { supplierIds?: string[] }) =>
    apiClient
      .get<
        PaginatedResponse<SupplierPerformancePurchaseOrderDetailRow>
      >(`/reports/supplier-performance/${encodeURIComponent(supplierKey)}/purchase-orders`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getSupplierPerformancePurchaseInvoices: (supplierKey: string, filters?: PharmaFilters & { supplierIds?: string[] }) =>
    apiClient
      .get<
        PaginatedResponse<SupplierPerformancePurchaseInvoiceDetailRow>
      >(`/reports/supplier-performance/${encodeURIComponent(supplierKey)}/purchase-invoices`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getStockOuts: (filters?: PharmaFilters) =>
    apiClient
      .get<StockOutReportResponse>('/reports/stock-out', { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getSalesPurchaseOverview: (kind: SalesPurchaseAnalysisKind, filters?: PharmaFilters) =>
    apiClient
      .get<SalesPurchaseOverviewResponse>(`${BASE}/analysis/${kind}/overview`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getSalesPurchaseBills: (kind: SalesPurchaseAnalysisKind, filters?: PharmaFilters) =>
    apiClient
      .get<
        PaginatedResponse<SalesPurchaseBillRow>
      >(`${BASE}/analysis/${kind}/bills`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getSalesPurchaseBillDrilldown: (kind: SalesPurchaseAnalysisKind, billKey: string) =>
    apiClient
      .get<SalesPurchaseBillDrilldown>(`${BASE}/analysis/${kind}/bills/${encodeURIComponent(billKey)}`)
      .then((r) => r.data),

  getSalesPurchaseItemDrilldown: (kind: SalesPurchaseAnalysisKind, itemKey: string, filters?: PharmaFilters) =>
    apiClient
      .get<SalesPurchaseItemDrilldown>(`${BASE}/analysis/${kind}/items/${encodeURIComponent(itemKey)}`, {
        params: toParams(filters ?? {}),
      })
      .then((r) => r.data),

  getSalesPurchasePartyDrilldown: (kind: SalesPurchaseAnalysisKind, partyCode: string, filters?: PharmaFilters) =>
    apiClient
      .get<SalesPurchasePartyDrilldown>(`${BASE}/analysis/${kind}/parties/${encodeURIComponent(partyCode)}`, {
        params: toParams(filters ?? {}),
      })
      .then((r) => r.data),

  getFinancialOutstanding: (
    filters?: PharmaFilters & {
      partyType?: FinancialPartyType;
      companyId?: number | string;
      asOfDate?: string;
      bucketBoundaries?: string;
      dsoDays?: number;
      /** Scope to one salesman's attributed parties ('__UNATTRIBUTED__' for none). */
      salesman?: string;
      route?: string;
      area?: string;
    },
  ) =>
    apiClient
      .get<FinancialOutstandingResponse>(`${BASE}/financial/outstanding`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getFinancialOutstandingByGroup: (
    filters?: PharmaFilters & {
      partyType?: FinancialPartyType;
      companyId?: number | string;
      asOfDate?: string;
      bucketBoundaries?: string;
    },
  ) =>
    apiClient
      .get<FinancialOutstandingGroupResponse>(`${BASE}/financial/outstanding-groups`, {
        params: toParams(filters ?? {}),
      })
      .then((r) => r.data),

  getFinancialOutstandingDetail: (
    partyCode: string,
    filters?: PharmaFilters & {
      companyId?: number | string;
      includeSettled?: boolean;
      asOfDate?: string;
      bucketBoundaries?: string;
      bucketIndex?: number;
    },
  ) =>
    apiClient
      .get<FinancialOutstandingDetailResponse>(`${BASE}/financial/outstanding/${encodeURIComponent(partyCode)}`, {
        params: toParams(filters ?? {}),
      })
      .then((r) => r.data),

  getFinancialPartyLedger: (
    partyCode: string,
    filters?: PharmaFilters & { companyId?: number | string; fromDate?: string; toDate?: string },
  ) =>
    apiClient
      .get<FinancialPartyLedgerResponse>(`${BASE}/financial/ledger/${encodeURIComponent(partyCode)}`, {
        params: toParams(filters ?? {}),
      })
      .then((r) => r.data),

  // ── Alerts ─────────────────────────────────────────────────────────────
  getAlerts: (config?: { nearExpiryDays?: number; aClassOnly?: boolean; alertLimit?: number }) =>
    apiClient.get<AlertItem[]>(`${BASE}/alerts`, { params: toParams(config ?? {}) }).then((r) => r.data),

  // ── Sales/Purchase analytics extensions ────────────────────────────────
  getSalesPurchaseDimension: (
    kind: SalesPurchaseAnalysisKind,
    dimension: SalesPurchaseDimension,
    filters?: PharmaFilters,
  ) =>
    apiClient
      .get<SalesPurchaseDimensionResponse>(`${BASE}/analysis/${kind}/dimension/${dimension}`, {
        params: toParams(filters ?? {}),
      })
      .then((r) => r.data),

  getSalesPurchaseComparison: (
    kind: SalesPurchaseAnalysisKind,
    filters: PharmaFilters & {
      startDate: string;
      endDate: string;
      compareStartDate: string;
      compareEndDate: string;
      dimension?: SalesPurchaseDimension | 'none';
    },
  ) =>
    apiClient
      .get<SalesPurchaseComparisonResponse>(`${BASE}/analysis/${kind}/comparison`, {
        params: toParams(filters),
      })
      .then((r) => r.data),

  getComparisonMonthCells: (kind: SalesPurchaseAnalysisKind, { keys, ...filters }: ComparisonMonthCellsFilters) =>
    apiClient
      .get<ComparisonMonthCellsResponse>(`${BASE}/analysis/${kind}/comparison/month-cells`, {
        // A list param would keep only its last key, so the keys travel as JSON.
        params: toParams({ ...filters, ...(keys ? { keys: JSON.stringify(keys) } : {}) }),
      })
      .then((r) => r.data),

  getSalesDegrowth: (
    kind: SalesPurchaseAnalysisKind,
    filters: PharmaFilters & {
      startDate: string;
      endDate: string;
      compareStartDate?: string;
      compareEndDate?: string;
      /** Comma-separated dimension list; backend defaults per kind when omitted. */
      dimensions?: string;
      minDropPct?: number;
      minBaseAmount?: number;
      perDimensionLimit?: number;
    },
  ) =>
    apiClient
      .get<SalesDegrowthResponse>(`${BASE}/analysis/${kind}/degrowth`, {
        params: toParams(filters),
      })
      .then((r) => r.data),

  getExecutiveSalesDashboard: (
    filters: PharmaFilters & {
      startDate: string;
      endDate: string;
      compareStartDate?: string;
      compareEndDate?: string;
    },
  ) =>
    apiClient
      .get<ExecutiveSalesDashboardResponse>(`${BASE}/dashboard/executive`, {
        params: toParams(filters),
      })
      .then((r) => r.data),

  // ── Day Summary 360° ────────────────────────────────────────────────────
  // Composes ~20 aggregations server-side, so it gets the long report timeout
  // rather than the default one.
  getDaySummary: (filters?: DaySummaryFilters) =>
    apiClient
      .get<DaySummaryResponse>(`${BASE}/dashboard/day-summary`, {
        params: toParams(filters ?? {}),
        timeout: THREE_SIXTY_REPORT_TIMEOUT_MS,
      })
      .then((r) => r.data),

  /**
   * The bills behind the Day Summary "Low Margin Sales" card. Server-side this
   * shares the card's own CTE and predicate, so `total` equals the card count.
   */
  getDaySummaryLowMarginBills: (filters: DaySummaryLowMarginFilters) =>
    apiClient
      .get<DaySummaryLowMarginResponse>(`${BASE}/dashboard/day-summary/low-margin-bills`, {
        params: toParams(filters),
      })
      .then((r) => r.data),

  // ── Month-wise pivot (Salesman → Customer → Item → Bill) ────────────────
  getMonthlyPivot: (kind: SalesPurchaseAnalysisKind, filters: MonthlyPivotFilters) =>
    apiClient
      .get<MonthlyPivotResponse>(`${BASE}/analysis/${kind}/monthly-pivot`, {
        params: toParams(filters),
        timeout: THREE_SIXTY_REPORT_TIMEOUT_MS,
      })
      .then((r) => r.data),

  // ── Degrowth member drill-down (Task 1) ─────────────────────────────────
  getDegrowthMemberCustomers: (
    kind: SalesPurchaseAnalysisKind,
    dimension: string,
    key: string,
    filters?: DegrowthMemberFilters,
  ) =>
    apiClient
      .get<DegrowthMemberResponse>(
        `${BASE}/analysis/${kind}/degrowth/${encodeURIComponent(dimension)}/${encodeURIComponent(key)}/customers`,
        { params: toParams(filters ?? {}) },
      )
      .then((r) => r.data),

  getSalesmanOutstanding: (
    filters?: PharmaFilters & {
      companyId?: number | string;
      salesman?: string;
      route?: string;
      area?: string;
      asOfDate?: string;
      bucketBoundaries?: string;
    },
  ) =>
    apiClient
      .get<SalesmanOutstandingResponse>(`${BASE}/financial/outstanding/salesman-wise`, {
        params: toParams(filters ?? {}),
      })
      .then((r) => r.data),

  /** `salesman` is a salesman code, or '__UNATTRIBUTED__'. */
  getSalesmanOutstandingBills: (
    filters: PharmaFilters & {
      salesman: string;
      companyId?: number | string;
      route?: string;
      area?: string;
      asOfDate?: string;
      bucketBoundaries?: string;
      bucketIndex?: number;
    },
  ) =>
    apiClient
      .get<SalesmanOutstandingBillsResponse>(`${BASE}/financial/outstanding/salesman-wise/bills`, {
        params: toParams(filters),
      })
      .then((r) => r.data),

  // ── Accounting Reports (Trial Balance + Account Ledger) ────────────────
  getTrialBalance: (
    filters?: PharmaFilters & { startDate?: string; endDate?: string; accountType?: string; showZero?: boolean },
  ) =>
    apiClient
      .get<TrialBalanceResponse>(`${BASE}/accounting/trial-balance`, { params: toParams(filters ?? {}) })
      .then((r) => r.data),

  getAccountLedger: (accountId: string, filters?: PharmaFilters & { startDate?: string; endDate?: string }) =>
    apiClient
      .get<AccountLedgerResponse>(`${BASE}/accounting/trial-balance/${encodeURIComponent(accountId)}/ledger`, {
        params: toParams(filters ?? {}),
      })
      .then((r) => r.data),

  // ── Export ─────────────────────────────────────────────────────────────
  exportReport: async (
    reportType: string,
    format: 'csv' | 'xlsx',
    filters?: Record<string, unknown>,
  ): Promise<Blob> => {
    const params = toParams({ ...filters, report: reportType, format });
    const response = await apiClient.get(`${BASE}/export`, {
      params,
      responseType: 'blob',
      timeout: 300000, // 5 minutes for large exports
    });
    return response.data;
  },
};
